/**
 * "Notices" in the client portal (R190, decision 1010 item 1; help `cloud.client-notices`; docs/specs/cloud/alerts.md
 * §11, docs/specs/portal/client-area.md §12). Each person of the client organisation chooses what they are told about
 * the organisation's projects, by email and by push on this device:
 * - what you receive: per kind (new reports, new documents, project status, block completed, daily line progress,
 *   flight started, flight landed), a Push and an Email tick, starting at the kind's default (the domain's
 *   `CLIENT_NOTICE_KINDS`: reports by email, flights off). A report's email is the report email Teslyn already sends,
 *   so its tick is the person's `report_email_choice` (also on My sign-in, Report emails);
 * - push on this device: the staff Alerts page's own service worker and Web Push keys (`alerts/push.ts`), and the
 *   browsers that receive the person's notices, with Remove;
 * - the organisation's notices of the last 30 days.
 * Preview as client shows the kinds with their defaults, read only, and the organisation's notices; no push.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CLIENT_NOTICE_EMAILS_PER_DAY,
  CLIENT_NOTICE_KINDS,
  CLIENT_NOTICE_PUSHES_PER_DAY,
  appEmailSenderName,
  clientNoticeChoice,
  clientNoticeWords,
  deviceShownName,
  type ClientNoticeGroup,
  type ClientNoticeKind,
  type ClientNoticePreference,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadMyDevices, loadPublicKey, removeDevice, saveDevice, type MyDevice } from '../alerts/api.ts';
import {
  browserPushEnv,
  currentPush,
  deviceLabel,
  pushSupport,
  turnOffPush,
  turnOnPush,
  type PushEnv,
} from '../alerts/push.ts';
import { loadProjects, type PortalProject } from '../portal/api.ts';
import { loadReportEmailChoice, saveReportEmailChoice } from '../setup/api.ts';
import { loadNoticeChoices, loadRecentNotices, saveNoticeChoice, type RecentNotice } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

export interface NoticesScreenProps {
  readonly sb: SupabaseClient;
  readonly userId: string;
  /** The sign-in's email address, where notice emails go. */
  readonly email: string;
  readonly clientOrgId: string;
  readonly clientOrgName: string;
  /** The operator (the tenant) that sends the notices. */
  readonly tenantName: string;
  /** Preview as client: read only, the defaults, no push. */
  readonly preview?: boolean;
  /** The browser's push objects (fakes in tests). */
  readonly pushEnv?: PushEnv;
}

const GROUPS: readonly ClientNoticeGroup[] = ['Reports and documents', 'Progress', 'Approvals', 'Flights'];

export function NoticesScreen({
  sb,
  userId,
  email,
  clientOrgId,
  clientOrgName,
  tenantName,
  preview = false,
  pushEnv,
}: NoticesScreenProps): ReactElement {
  const env = useMemo(() => pushEnv ?? browserPushEnv(), [pushEnv]);
  const [publicKey, setPublicKey] = useState<string | null | undefined>(undefined);
  const [devices, setDevices] = useState<MyDevice[] | null>(null);
  const [here, setHere] = useState<string | null>(null);
  const [choices, setChoices] = useState<Map<string, ClientNoticePreference> | null>(null);
  const [reportEmails, setReportEmails] = useState<boolean>(true);
  const [recent, setRecent] = useState<RecentNotice[] | null>(null);
  const [projects, setProjects] = useState<Map<string, PortalProject>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const support = pushSupport(env);

  const reload = useCallback(() => {
    setError(null);
    const own = preview
      ? Promise.resolve([null, [], [], true, null] as const)
      : Promise.all([
          loadPublicKey(sb),
          loadMyDevices(sb, userId),
          loadNoticeChoices(sb, userId, clientOrgId),
          loadReportEmailChoice(sb, clientOrgId),
          currentPush(env).catch(() => null),
        ] as const);
    Promise.all([
      own,
      loadRecentNotices(sb, clientOrgId),
      loadProjects(sb, { kind: 'client', clientOrgId }).catch(() => [] as PortalProject[]),
    ])
      .then(([[key, devs, ch, reports, cur], rec, proj]) => {
        setPublicKey(key);
        setDevices([...devs]);
        setChoices(new Map(ch.map((c) => [c.kind, c])));
        setReportEmails(reports);
        setHere(cur && devs.some((d) => d.endpoint === cur.endpoint) ? cur.endpoint : null);
        setRecent(rec);
        setProjects(new Map(proj.map((p) => [p.id, p])));
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, userId, clientOrgId, env, preview]);
  useEffect(reload, [reload]);

  const run = (what: string, job: () => Promise<string | void>): void => {
    setBusy(what);
    setNotice(null);
    job()
      .then((text) => text && setNotice({ ok: true, text }))
      .catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
      .finally(() => setBusy(null));
  };

  const turnOn = (): void =>
    run('push', async () => {
      if (!publicKey) throw new Error(`Push notices are not set up yet. Ask ${tenantName}.`);
      const p = await turnOnPush(env, publicKey);
      await saveDevice(sb, p, deviceLabel(env.userAgent), window.location.origin);
      reload();
      return 'Push notices are on for this device.';
    });

  const turnOff = (): void =>
    run('push', async () => {
      const endpoint = await turnOffPush(env);
      if (endpoint) await removeDevice(sb, { endpoint });
      reload();
      return 'Push notices are off for this device.';
    });

  const remove = (d: MyDevice): void =>
    run(`remove:${d.id}`, async () => {
      await removeDevice(sb, { id: d.id });
      if (d.endpoint === here) await turnOffPush(env).catch(() => null);
      reload();
      return `${deviceShownName({ kind: 'browser', name: d.device_name, label: d.device_label })} no longer receives push notices.`;
    });

  const choose = (k: ClientNoticeKind, channel: 'push' | 'email', on: boolean): void => {
    if (channel === 'email' && k.emailBy === 'report_email') {
      setReportEmails(on);
      run(`choice:${k.id}:email`, async () => {
        try {
          await saveReportEmailChoice(sb, userId, clientOrgId, on);
        } catch (e) {
          setReportEmails(!on);
          throw e;
        }
        return on ? 'You will be emailed about new reports.' : 'You will not be emailed about new reports.';
      });
      return;
    }
    const was = choices?.get(k.id) ?? { user_id: userId, kind: k.id, push: null, email: null };
    const next = { ...was, [channel]: on };
    setChoices((m) => new Map(m ?? []).set(k.id, next));
    run(`choice:${k.id}:${channel}`, async () => {
      try {
        await saveNoticeChoice(sb, userId, clientOrgId, next);
      } catch (e) {
        setChoices((m) => new Map(m ?? []).set(k.id, was));
        throw e;
      }
      return `${k.label}: saved.`;
    });
  };

  const loaded = publicKey !== undefined && devices !== null && choices !== null && recent !== null;

  return (
    <section className="page">
      <PageHeader
        title="Notices"
        subtitle={`${tenantName} can tell you about ${clientOrgName}’s projects by email and by push on your phone or laptop. Choose what you receive.`}
      />
      {preview && (
        <div className="notice" role="note">
          <p className="body">
            Preview: each person of {clientOrgName} chooses their own notices on this page. The ticks show
            what a person receives before choosing. Nothing can be changed here.
          </p>
        </div>
      )}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && !loaded && <Loading what="your notices" />}
      {loaded && (
        <>
          <div className="panel stack">
            <h2 className="heading">What you receive</h2>
            <p className="caption muted">
              Emails come from “{appEmailSenderName(tenantName)}”{preview ? '' : ` to ${email}`}. Push notices
              reach the devices where you turned push on, below. At most {CLIENT_NOTICE_EMAILS_PER_DAY} notice
              emails and {CLIENT_NOTICE_PUSHES_PER_DAY} push notices reach you in 24 hours. Flight notices
              give the aircraft’s take-off, landing and flight time; its position is on each project’s Map.
            </p>
            {GROUPS.map((g) => (
              <div key={g} className="stack">
                <h3 className="label muted">{g}</h3>
                <table className="table alerts-table" aria-label={g}>
                  <colgroup>
                    <col className="alerts-table__col-alert" />
                    <col className="alerts-table__col-choice" />
                    <col className="alerts-table__col-choice" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="label">Notice</th>
                      <th className="label">Push</th>
                      <th className="label">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLIENT_NOTICE_KINDS.filter((k) => k.group === g).map((k) => {
                      const c = clientNoticeChoice(k.id, choices.get(k.id) ?? null, reportEmails);
                      return (
                        <tr key={k.id}>
                          <td className="alerts-table__alert">
                            <span className="body">{k.label}</span>
                            <br />
                            <span className="caption muted">
                              {k.description}
                              {k.emailBy === 'report_email'
                                ? ' The email is the report email, with the link or the PDF as published.'
                                : ''}
                            </span>
                          </td>
                          {(['push', 'email'] as const).map((ch) => (
                            <td key={ch} className="alerts-table__choice">
                              <label className="body row check">
                                <input
                                  type="checkbox"
                                  aria-label={`${k.label} by ${ch}`}
                                  checked={c[ch]}
                                  disabled={preview || busy === `choice:${k.id}:${ch}`}
                                  onChange={(e) => choose(k, ch, e.target.checked)}
                                />
                                <span className="alerts-table__head">{ch === 'push' ? 'Push' : 'Email'}</span>
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {!preview && (
            <div className="panel stack">
              <h2 className="heading">Push on this device</h2>
              {support === 'unsupported' ? (
                <p className="body">
                  This browser cannot receive push notices. On an iPhone or iPad, add this site to the home
                  screen (Share, Add to Home Screen) and open it from there; elsewhere use Chrome, Edge,
                  Firefox or Safari.
                </p>
              ) : support === 'blocked' ? (
                <p className="body">
                  Notifications are blocked for this site. Allow them in the browser’s site settings, then
                  turn push on here.
                </p>
              ) : publicKey === null ? (
                <p className="body">Push notices are not set up yet. Ask {tenantName}.</p>
              ) : here ? (
                <p className="body">On: this device receives your push notices.</p>
              ) : (
                <p className="body">Off: this device does not receive push notices.</p>
              )}
              {support === 'ready' && publicKey && (
                <div className="row">
                  {here ? (
                    <Button onClick={turnOff} busy={busy === 'push'}>
                      {busy === 'push' ? 'Turning off…' : 'Turn off push on this device'}
                    </Button>
                  ) : (
                    <Button variant="primary" onClick={turnOn} busy={busy === 'push'}>
                      {busy === 'push' ? 'Turning on…' : 'Turn on push on this device'}
                    </Button>
                  )}
                </div>
              )}
              <h3 className="label muted">Your devices</h3>
              {devices.length === 0 ? (
                <p className="body muted">No device receives your push notices yet.</p>
              ) : (
                <ul className="stack">
                  {devices.map((d) => {
                    const shown = deviceShownName({
                      kind: 'browser',
                      name: d.device_name,
                      label: d.device_label,
                    });
                    return (
                      <li key={d.id} className="row row--wrap">
                        <span className="body">
                          <strong>
                            {shown}
                            {d.endpoint === here ? ' (this device)' : ''}
                          </strong>{' '}
                          <span className="caption muted">· added {when(d.created_at)}</span>
                        </span>
                        <Button
                          variant="quiet"
                          aria-label={`Remove ${shown}`}
                          busy={busy === `remove:${d.id}`}
                          onClick={() => remove(d)}
                        >
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="panel stack">
            <h2 className="heading">Recent notices</h2>
            {recent.length === 0 ? (
              <p className="body muted">No notice about {clientOrgName}’s projects in the last 30 days.</p>
            ) : (
              <ul className="stack" aria-label="Recent notices">
                {recent.map((n) => {
                  const p = n.project_id ? projects.get(n.project_id) : undefined;
                  const w = clientNoticeWords(n, {
                    tenantName,
                    projectNumber: p?.number ?? '',
                    projectTitle: p?.title ?? '',
                  });
                  return (
                    <li key={n.id} className="row row--wrap">
                      <span className="body">{w.title}</span>
                      <span className="caption muted">
                        {when(n.occurred_at)}
                        {n.status === 'pending' ? ' · being sent' : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
